import React from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useComponentTheme } from '../useComponentTheme';
import { Icon } from '../icons';
import { Text } from '../text/Text';
import { withSpacingStyles } from '../withSpacingStyles';

type Props = {
  payByCardAction?: () => void,
  payAtCounterAction?: () => void,
  mode: 'default' | 'Kiosk',
  payByCardSelected: boolean,
  payAtCounterSelected: boolean,
  payByCardText: string,
  payAtCounterText: string,
  extraText: string,
  className?: string,
  isMobile?: boolean
}

const modeStyles = {
  'Kiosk': {
    container: {
      flexDirection: 'column',
    },
  },
  'default': {
    container: {
      flexDirection: 'row',
    },
  },
};

const _PaymentMethodCards = ({
  payByCardAction,
  payAtCounterAction,
  mode,
  payByCardSelected,
  payAtCounterSelected,
  payByCardText,
  payAtCounterText,
  extraText,
  className,
  isMobile
}: Props) => {
  const componentTheme = useComponentTheme(theme => theme.components.PaymentMethodCards, mode);
  const theme = useTheme();

  return (
    <Container mode={mode} className={className}>
      <Card data-test="payment-method-card" selected={payByCardSelected} onClick={payByCardAction} hasImage={!!componentTheme.payByCardImage && mode === 'Kiosk'}>
        {payByCardSelected && (
          <CloseBtnWrapper isMobile={isMobile}>
            <Icon name="CheckCircle" size={isMobile ? 20 : 24} fill={theme.color.brand}/>
          </CloseBtnWrapper>
        )}
        <CardContent>
          {!!componentTheme.payByCardImage && mode === 'Kiosk' && <ImageHolder src={componentTheme.payByCardImage} alt="" />}
          {!!componentTheme.payByCardIcon && (!componentTheme.payByCardImage || mode === 'default') && <Icon name={componentTheme.payByCardIcon} size={isMobile ? 24 : 32}/>}
          <PayByCardText align="center" level={isMobile ? 3 : 2} componentTheme={componentTheme}>{payByCardText}</PayByCardText>
        </CardContent>
      </Card>

      <Card data-test="payment-method-counter" selected={payAtCounterSelected} onClick={payAtCounterAction} mode={mode}>
        {payAtCounterSelected && (
          <CloseBtnWrapper>
            <Icon name="CheckCircle" size={isMobile ? 20 : 24} fill={theme.color.brand}/>
          </CloseBtnWrapper>
        )}
        <CardContent>
        {!!componentTheme.payAtCounterImage && mode === 'Kiosk' && <ImageHolder src={componentTheme.payAtCounterImage} alt="" />}
        {!!componentTheme.payAtCounterIcon && (!componentTheme.payAtCounterImage || mode === 'default') && <Icon name={componentTheme.payAtCounterIcon} size={isMobile ? 24 : 32}/>}
          <PayAtCounterText align="center" level={isMobile ? 3 : 2} componentTheme={componentTheme}>{payAtCounterText}</PayAtCounterText>
        </CardContent>
      </Card>

      {mode === 'Kiosk' && !!extraText && extraText !== 'counterExtraText' && <ExtraText align="center" level={3} componentTheme={componentTheme}>{extraText}</ExtraText>}

    </Container>
  );
};

_PaymentMethodCards.defaultProps = {
  mode: 'default',
};

_PaymentMethodCards.displayName = 'PaymentMethodCards';

export const PaymentMethodCards = withSpacingStyles(_PaymentMethodCards);

const Container = styled('div')`
  display: flex;
  flex-direction: ${({ mode }) => modeStyles[mode].container.flexDirection };
  gap: 24px;
`;

const Card = styled('div')`
  display: flex;
  padding: ${({ hasImage }) => hasImage ? '24px' : '16px' };
  width: 100%;
  position: relative;
  justify-content: center;
  border: ${({ theme, selected }) => selected ? `2px solid ${theme.color.brand}` : theme.stroke.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
`;

const CloseBtnWrapper = styled('div')`
  position: absolute;
  right: ${({ isMobile }) => isMobile ? '8px;' : '16px;'};
  top: ${({ isMobile }) => isMobile ? '8px;' : '16px;'};
`;

const CardContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const ImageHolder = styled('img')`
  width: 100%;
  height: auto;
  margin-bottom: 16px;
  padding: 0 32px;
`;

const PayByCardText = styled(Text)`
  ${({ componentTheme }) => !!componentTheme.payByCardTextFontWeight && `font-weight: ${componentTheme.payByCardTextFontWeight};`}
`;

const PayAtCounterText = styled(Text)`
  ${({ componentTheme }) => !!componentTheme.payAtCounterTextFontWeight && `font-weight: ${componentTheme.payAtCounterTextFontWeight};`}
`;

const ExtraText = styled(Text)`
  ${({ componentTheme }) => !!componentTheme.extraTextColor && `color: ${componentTheme.extraTextColor};`}
  ${({ componentTheme }) => !!componentTheme.extraTextLineHeight && `line-height: ${componentTheme.extraTextLineHeight};`}
  ${({ componentTheme }) => !!componentTheme.extraTextFontWeight && `font-weight: ${componentTheme.extraTextFontWeight};`}
  ${({ componentTheme }) => !!componentTheme.extraTextFontSize && `font-size: ${componentTheme.extraTextFontSize};`}
`;
